import { useQuery, useQueryClient } from 'react-query';
import { getContext } from '@fiverr-private/fiverr_context';
import { fetchSellerDocuments, fetchTemplates, TemplateResponseObject } from '../api';
import { TemplatesDataContextProps } from '../components/TemplatesDataContext/types';

export const TEMPLATES_QUERY_KEY = 'templates';
export const SELLER_TEMPLATES_QUERY_KEY = 'seller_templates';

export interface useTemplatesDataOptions {
    autoFetch?: boolean;
    sellerId?: number;
}

export const useTemplatesData = ({
    autoFetchTemplates,
    autoFetchSellerDocuments,
    sellerId,
    initialDocuments,
    withTemplatesUsage = true,
    withInactiveTemplates = false,
}: Omit<TemplatesDataContextProps, 'children'> = {}) => {
    const queryClient = useQueryClient();
    const { userId, organizationId } = getContext();

    const templatesQuery = useQuery({
        queryKey: [TEMPLATES_QUERY_KEY],
        queryFn: () =>
            fetchTemplates(userId, organizationId, {
                withUsage: withTemplatesUsage,
                withInactive: withInactiveTemplates,
            }),

        enabled: autoFetchTemplates,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: Infinity,
        initialData: initialDocuments,
    });

    const pollTemplateQueries = (
        delay = 1000,
        maxDelay = 30000,
        maxAttempts = 10,
        predicate = (data: TemplateResponseObject[] | undefined) =>
            !data?.length || data.some((item) => item.virusScanStatus === 'in_progress')
    ) => {
        let attempt = 0;

        const runInvalidate = async () => {
            if (attempt >= maxAttempts) return;

            attempt++;
            const queryData = await templatesQuery.refetch();

            const shouldInvalidate = predicate(queryData?.data);

            if (shouldInvalidate) {
                setTimeout(runInvalidate, delay);
                delay = Math.min(delay * 2, maxDelay);
            }
        };
        runInvalidate();
    };

    const invalidateSellerDataQueries = () => {
        queryClient.invalidateQueries({
            queryKey: [SELLER_TEMPLATES_QUERY_KEY, sellerId?.toString()],
        });
    };

    const sellerDataQuery = useQuery({
        queryKey: [SELLER_TEMPLATES_QUERY_KEY, sellerId?.toString()],
        queryFn: () => (sellerId ? fetchSellerDocuments(sellerId) : undefined),
        enabled: autoFetchSellerDocuments && !!sellerId,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: Infinity,
    });

    return {
        pollTemplateQueries,
        invalidateSellerDataQueries,
        templatesQuery,
        sellerDataQuery,
    };
};
